import { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useMutation } from '@apollo/client';
import JSZip from 'jszip';

import { LoadingLogo } from '../../../../devtools/components/Modal';
import { 
  DeleteEmojiPackMutation,
  DeleteEmojiPackMutationVariables,
  Emoji,
  EmojiAvailabilityType,
  EmojiPack,
  EmojiPackLayout,
  EmojiPacksLayoutAvailabilitiesQuery,
  UpdateEmojiPackMutation,
  UpdateEmojiPackMutationVariables,
  UploadBatchEmojiAssetMutation,
  UploadBatchEmojiAssetMutationVariables,
  UploadEmojiPackAssetMutation,
  UploadEmojiPackAssetMutationVariables
} from '../../../../../../__gqltypes__';
import DeleteItemModal from './DeleteItemModal';
import DragUpload from './DragUpload';

import { DELETE_EMOJI_PACK, UPDATE_EMOJI_PACK, UPLOAD_BATCH_EMOJI_ASSET, UPLOAD_EMOJI_PACK_ASSET } from '../../../components/apolloQueries';
import EmojiView from './EmojiView';
import EmojiFactory, { EmojiSource } from './EmojiFactory';
import OwnershipStatsCard from '../../../../devtools/screens/VanityItemTools/OwnershipStatsCard';
import ExpandableFormControl from '../../../components/ExpandableFormControl';
import { ClipboardButton } from '../../../../curation/screens/SongSearch/components/ClipboardButton';


type EPLayoutAvailabilities = EmojiPacksLayoutAvailabilitiesQuery['liveOps']['emojiPacksLayoutAvailabilities'][0];

type Props = {
  item: EmojiPack;
  refetch: () => void;
  onDelete: (id: string) => void;
  epLayoutAvai?: EPLayoutAvailabilities,
};


export default function EmojiPackView({item, refetch, onDelete, epLayoutAvai}: Props): JSX.Element {
  const [itemData, setItemData] = useState(item);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newIndices, setNewIndices] = useState<Array<number>>([]);
  const [showUpload, setShowUpload] = useState(false);
  const [emojiSources, setEmojiSources] = useState<EmojiSource[]>([]);
  const [showFactory, setShowFactory] = useState(false);
  
  useEffect(() => {
    setItemData(item);
    setNewIndices([]);
    setShowUpload(item && (!item.asset || item.asset.length === 0));
    setShowFactory(!item || item.emojis.length === 0);
  }, [item]);

  const reloadEmojiPacks = useCallback(() => {
    refetch();
  }, [refetch]);

  const [updateItem, {loading: updateLoading}] = useMutation<
    UpdateEmojiPackMutation, 
    UpdateEmojiPackMutationVariables
  >(UPDATE_EMOJI_PACK, { onCompleted: reloadEmojiPacks });

  const [deleteItem, {loading: deleteLoading}] = useMutation<
    DeleteEmojiPackMutation, 
    DeleteEmojiPackMutationVariables
  >(DELETE_EMOJI_PACK, { onCompleted: reloadEmojiPacks });

  const [uploadEPAsset, { loading: uploadLoading}] = useMutation<
    UploadEmojiPackAssetMutation,
    UploadEmojiPackAssetMutationVariables
  >(UPLOAD_EMOJI_PACK_ASSET);

  const [uploadBatchEAsset, { loading: uploadBatchLoading}] = useMutation<
    UploadBatchEmojiAssetMutation,
    UploadBatchEmojiAssetMutationVariables
  >(UPLOAD_BATCH_EMOJI_ASSET);


  const loading = updateLoading || deleteLoading || uploadLoading || uploadBatchLoading;
  const handleUpdate = useCallback((newData: EmojiPack) => {
    updateItem({
      variables: {
        input: {
          id: newData.id,
          name: newData.name,
          asset: newData.asset,
          layout: newData.layout,
          emojis: newData.emojis.map((emoji) => ({
            id: emoji.id,
            asset: emoji.asset,
            name: emoji.name,
            utfCode: emoji.utfCode,
          })),
          isDraft: newData.isDraft,
        }
      }
    }).then(() => {
      setNewIndices([]);
    });
  }, [updateItem]);

  const handleDelete = () => {
    deleteItem({variables: {
      id: itemData.id,
    }}).finally(() => {
      onDelete(itemData.id);
      setShowDeleteModal(false);
    });
  };

  const handleEPUpload = useCallback((file: Blob) => {
    uploadEPAsset({variables: {
      input: {
        id: item.id,
        file,
      }
    }}).then(({data}) => {
      setItemData((prev) => {
        const newData = {...prev};
        if (data) {
          newData.asset = data?.uploadEmojiPackAsset.emojiPack.asset;
        }
        return newData;
      });
    });
  }, [item.id, uploadEPAsset]);

  const cleanupSources = useCallback(() => {
    setEmojiSources((prev) => {
      prev.forEach((src) => {
        if (src && src.preview) {
          URL.revokeObjectURL(src.preview);
        }
      });
      return [];
    });
  }, []);

useEffect(() => {
    return cleanupSources;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClean = useCallback(() => {
    cleanupSources();
    setNewIndices([]);
    setItemData((prev) => ({...prev, emojis: [] }));
  }, [cleanupSources]);

  const onEmojiSources = useCallback((sources: EmojiSource[]) => {
    const emojis = new Array<Emoji>(sources.length);
    const indices = new Array<number>(sources.length);
    sources.forEach((source, index) => {
      emojis[index] = source.emoji;
      indices[index] = index;
    });
    setItemData((prev) => ({ ...prev, emojis }));
    cleanupSources();
    setEmojiSources(sources);
    setNewIndices(indices);
  }, [cleanupSources]);

  const idBuilder = useCallback((utfCode: string) => {
    return `${itemData.id}.${utfCode}`;
  }, [itemData.id]);


  const onGenerate = useCallback(() => {
    const archive = new JSZip();
    emojiSources.forEach((source) => {
      const {file, emoji, ext} = source;
      const codePoint_ = emoji.name.codePointAt(0) ?? 0; 
      const codePoint = codePoint_.toString(16);
      archive.file(`${codePoint}_${emoji.utfCode}.${ext}`, file.arrayBuffer(), {binary: true});
    });
    archive.generateAsync({type:'blob'}).then((fileBlob) => {
      return uploadBatchEAsset({
          variables: {
          input: {
            file: fileBlob
          }
        }
      });
    }).then(({data}) => {
        if (data?.uploadBatchEmojiAsset) {
          const emojis = itemData.emojis;
          data.uploadBatchEmojiAsset.assets.forEach((asset, index) => {
            emojis[index].asset = asset;
          });
          setItemData((prev) => ({...prev, emojis}));
          cleanupSources();
          setShowFactory(false);
        } else {
          onClean();
        }
      }).catch(() => {
        onClean();
      });
  }, [emojiSources, uploadBatchEAsset, itemData.emojis, cleanupSources, onClean]);

  const handleAddEmoji = useCallback(() => {
    const e: Emoji = {
      __typename: 'Emoji',
      name: 'New Emoji',
      asset: '',
      utfCode: 'F8FF',
      cost: {
        __typename: 'CurrencyDiamond',
        amount: 0,
        id: 'CurrencyDiamon',
      },
      id: itemData.id,
    };

    setNewIndices((prev) => prev.concat([itemData.emojis.length]));
    setItemData((prev) => ({...prev, emojis: prev.emojis.concat(e)}));
  }, [itemData]);

  const handleDeleteEmoji = useCallback((index: number) => {
    const newEmojis = itemData.emojis.filter((val, idx) => idx !== index);
    const newItemData = {...itemData, emojis: newEmojis};
    setNewIndices((prev) => prev.filter((val) => val !== index));
    setItemData(newItemData);
    handleUpdate(newItemData);
  }, [handleUpdate, itemData]);
  
  const handleMoveEmoji = useCallback((index: number, isUp: boolean) => {
    const inc = isUp ? -1 : 1;
    const newEmojis = [...itemData.emojis];
    [newEmojis[index], newEmojis[index + inc]] = [newEmojis[index + inc], newEmojis[index]];
    setItemData((prev) => ({...prev, emojis: newEmojis}));
    const newSources = [...emojiSources];
    [newSources[index], newSources[index + inc]] = [newSources[index + inc], newSources[index]];
    setEmojiSources(newSources);
  }, [emojiSources, itemData.emojis]);


  const styles = {
    img: {
      display: 'block',
      width: 96,
      height: 64,
    },
  };

  if (!itemData) {
    return <></>;
  }

  let maxEmojis = 30;
  if (itemData.layout === EmojiPackLayout.IAP) {
    maxEmojis = 15;
  }

  return (
    <>
      <Card className="mt-3">
        <LoadingLogo show={loading} />
        <DeleteItemModal
          title={`Delete "${itemData?.name}" Emoji Pack?`}
          show={showDeleteModal && !loading}
          onHide={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
        />
        <Card.Header className="text-center d-flex justify-content-between">
          <h4>{itemData?.name}</h4>
          <div>
            <Button className="mr-3" variant="danger" onClick={() => setShowDeleteModal(true)}>
              Delete
            </Button>
            <Button onClick={() => handleUpdate(itemData)}>Save</Button>
          </div>
        </Card.Header>
        <Card.Body style={{ height: '100%' }}>
          <Form>
            <Row className="mb-3 align-items-end">
              <Form.Group as={Col} xs="auto">
                <Form.Label>Id</Form.Label>
                <Form.Control placeholder={itemData.id} disabled />
              </Form.Group>
              <Form.Group as={Col} xs="auto">
                <ClipboardButton title="Copy ID" textToCopy={itemData.id} />
              </Form.Group>
              <Form.Group as={Col} xs="auto">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  placeholder=""
                  value={itemData.name}
                  onChange={(e) => setItemData({ ...itemData, name: e.target.value })}
                />
              </Form.Group>
              <Form.Group as={Col} xs="auto">
                <Form.Label>Layout</Form.Label>
                <Form.Control 
                  as="select"
                  value={itemData.layout} 
                  onChange={(e) => setItemData({...itemData, layout: e.target.value as EmojiPackLayout})}>
                  {Object.keys(EmojiPackLayout).map((type) => (
                    <option value={type} key={type}>
                      {type}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
              <Form.Group as={Col} xs="auto">
                <Form.Check 
                  type='checkbox'
                  checked={itemData?.isDraft}
                  label="Is draft"
                  onChange={() => setItemData({ ...itemData, isDraft: !itemData.isDraft })}
                />
              </Form.Group>
            </Row>
            <Row className="mb-3 align-items-end">
              <Form.Group as={Col} xs="auto">
                <Form.Label>Asset</Form.Label>
                <ExpandableFormControl 
                  placeholder={itemData.asset}
                  disabled
                  expandedWidth={600} />
              </Form.Group>
              <Form.Group as={Col} xs="auto">
                <ClipboardButton title="Copy URL" textToCopy={itemData.asset ?? ''} />
              </Form.Group>
              <Form.Group as={Col} xs="auto">
                <Button onClick={() => setShowUpload(val => !val)}>
                  Upload new asset  <i className="fas fa-upload" />
                </Button>
              </Form.Group>
            </Row>
            <Row>
              {!!itemData.asset && (
                <Form.Group as={Col} xs="auto">
                  <img src={itemData.asset} alt={itemData.name} style={styles.img} />
                </Form.Group>
              )}
              <Form.Group as={Col} xs="auto">
                {showUpload && (<DragUpload onUpload={handleEPUpload} />)}
              </Form.Group>
            </Row>
            {showFactory && !loading && (
              emojiSources.length === 0 ? (
                <EmojiFactory idBuilder={idBuilder} onEmojiSources={onEmojiSources}/>
              ) : (
                <div className="mt-2">
                  <Button className='mr-2' onClick={onClean}>
                    <i className="fas fa-recycle mr-2" />
                    Clear
                  </Button>
                  <Button onClick={onGenerate}variant='success'>
                    <i className="fas fa-spinner mr-2" />
                    Generate
                  </Button>
                </div>
              )
            )}
            {!loading && itemData.emojis.length > 0 && (
                <Form.Group className="mt-3">
                  {itemData.emojis.length < maxEmojis && (
                    <Button className="mb-4" onClick={handleAddEmoji}>Add Emoji</Button> 
                  )}
                  <h4>Emojis</h4>
                  {itemData.emojis.map((emoji, index) => {
                    const source = emojiSources.length > index 
                      ? emojiSources[index].preview 
                      : null;
                    return (
                    <EmojiView 
                      key={emoji.id}
                      item={emoji}
                      index={index}
                      source={source}
                      isNew={newIndices.includes(index)}
                      emojiPackSize={itemData.emojis.length}
                      onUpdate={reloadEmojiPacks}
                      onDelete={handleDeleteEmoji}
                      onMove={handleMoveEmoji}
                      availability={epLayoutAvai ? epLayoutAvai.availability[index] : EmojiAvailabilityType.FREE}
                      />
                    );
                  })}
                </Form.Group>
            )}
          </Form>
        </Card.Body>
      </Card>
      {itemData?.id && <OwnershipStatsCard category="EMOJI_PACK" itemId={itemData.id} />}
    </>
  );
}